import React, { ReactElement, useRef, MouseEvent, useState, useEffect, useCallback } from 'react';
import Popover, { IPopoverRef, PopoverPlacement } from 'shared/components/Popover';
import { useHistory } from 'react-router-dom';
import { IOption } from 'shared/components/MutiSelectSearchDropdown';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { Flex, Box, TextInput, BackgroundProps, Alert, Text, HStack } from '@televet/kibble-ui';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useAuth } from 'shared/providers/AuthProvider';
import { ClinicAvatar } from 'shared/components/Avatars';

interface IChangeClinicPopoverProps {
  anchor: ReactElement;
  clinicOptions: IOption[];
  onClinicSelected: (e: MouseEvent, option: IOption) => Promise<void>;
  placement?: PopoverPlacement;
  yOffset?: number;
  onKeepPopoverOpen?: () => void;
}

const ChangeClinicPopover = ({
  anchor,
  clinicOptions,
  onClinicSelected,
  placement,
  yOffset,
  onKeepPopoverOpen,
}: IChangeClinicPopoverProps): JSX.Element => {
  const popoverRef = useRef<IPopoverRef>();
  const history = useHistory();
  const { setIsOpen } = useSidePanel();
  const { currentClinicId } = useClinicUser();
  const { isImpersonating } = useAuth();
  const [searchedClinic, setSearchedClinic] = useState('');
  const [clinicOptionList, setClinicOptionList] = useState<IOption[]>([]);

  useEffect(() => {
    setClinicOptionList(clinicOptions);
  }, [clinicOptions]);

  const onClinicSearch = useCallback(
    (e): void => {
      setSearchedClinic(e.target.value);
      setClinicOptionList(
        clinicOptions.filter((option: IOption) => option.text?.toLowerCase().includes(e.target.value.toLowerCase())),
      );
    },
    [clinicOptions],
  );

  const handleKeyboardNavigation = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      popoverRef.current?.hide();
    }
  }, []);

  return (
    <Popover anchor={anchor} placement={placement} yOffset={yOffset} disabled={clinicOptions.length === 1} isLazy>
      <Box pt={2}>
        {isImpersonating && (
          <Flex align="center" justify="center" px={2}>
            <Alert
              maxW="260px"
              description="Changing the current clinic here will not affect the user's account."
              status="info"
              hideCloseButton
              mb={2}
              hideIcon
              descriptionProps={{ fontSize: 'sm', children: null }}
            />
          </Flex>
        )}
        {clinicOptions.length > 6 && (
          <TextInput
            py={2}
            mx={2}
            mb={2}
            placeholder="Search for clinic"
            value={searchedClinic}
            autoFocus
            onClick={(): void => onKeepPopoverOpen && onKeepPopoverOpen()}
            onChange={(e): void => onClinicSearch(e)}
            onKeyDown={(e): void => handleKeyboardNavigation(e)}
          />
        )}
        <Flex flexDirection="column" maxW="276px" maxH="262px" overflowY="auto" pb={2}>
          {clinicOptionList.map((clinic) => {
            const isSelectedClinic = clinic.value === currentClinicId;

            if (clinic.text && clinic.value) {
              const clinicNameParts = clinic.text.split(' ');

              return (
                <Box
                  key={clinic.value}
                  width="100%"
                  background={isSelectedClinic ? 'background.primaryAlpha.hover' : 'background.default'}
                >
                  <ButtonComponent
                    key={clinic.value}
                    onClick={(e): void => {
                      onClinicSelected(e, clinic);
                      history.push('/');
                      setIsOpen(false);
                      popoverRef?.current?.hide();
                    }}
                  >
                    <ClinicAvatar
                      clinicMember={{
                        id: clinic.value,
                        firstName: clinicNameParts[0],
                        lastName: clinicNameParts[1] || '',
                        profilePic: clinic.logo,
                        displayName: clinic.text,
                      }}
                      hideBorder={!isSelectedClinic}
                      showTooltip={false}
                      size="xs"
                    />
                    <Text size="sm" isTruncated>
                      {clinic.text}
                    </Text>
                  </ButtonComponent>
                </Box>
              );
            }
            return null;
          })}
        </Flex>
      </Box>
    </Popover>
  );
};

export const ButtonComponent = ({
  children,
  onClick,
  ...rest
}: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<Element, globalThis.MouseEvent>) => void;
} & BackgroundProps): JSX.Element => {
  return (
    <HStack
      onClick={onClick}
      width="100%"
      px={3}
      py={2}
      spacing={2}
      cursor="pointer"
      _hover={{ bg: 'background.primaryAlpha.hover' }}
      {...rest}
    >
      {children}
    </HStack>
  );
};

export default ChangeClinicPopover;
